import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';
import { useMeasure, useToggle } from 'react-use';
import { useEventHubContext } from 'src/contexts/EventHubContext';
import { useGetSectionRowScoreRecommendations } from 'src/hooks/api/useGetSectionRowScoreRecommendations';
import {
  Event,
  Listing,
  SectionRowScoreRecommendations,
  SelectedSectionSettings,
  VenueMapInfo,
} from 'src/WebApiController';

export const useListingScoreRecommendations = (
  event: Event | undefined,
  venueMapInfo?: VenueMapInfo | null,
  selectedListing?: Listing | undefined,
  disabled?: boolean
) => {
  const { selectedSectionRecommendations } =
    useGetSectionRowScoreRecommendations(
      event?.viagId ?? undefined,
      venueMapInfo?.venueCfgId,
      selectedListing?.rowId ?? undefined,
      disabled ||
        !isEmpty(selectedListing?.compSectionSettings?.sectionIdFilter)
    );

  const recommendedSelectedSectionSettings = useMemo(() => {
    if (disabled) {
      return undefined;
    }
    if (
      !selectedListing?.rowId ||
      !selectedSectionRecommendations?.rowRecommendations
    ) {
      return undefined;
    }

    return selectedSectionRecommendations.rowRecommendations[
      selectedListing.rowId
    ];
  }, [
    disabled,
    selectedListing?.rowId,
    selectedSectionRecommendations?.rowRecommendations,
  ]);

  const sectionRowScoreRecommendations = useMemo(():
    | (SectionRowScoreRecommendations & { listingId: number })
    | undefined => {
    if (
      !(
        selectedListing?.id &&
        event?.viagId &&
        venueMapInfo?.venueCfgId &&
        selectedSectionRecommendations?.modelVersionId &&
        recommendedSelectedSectionSettings
      )
    ) {
      return undefined;
    }

    return {
      listingId: selectedListing?.id,
      eventId: event.viagId,
      venueConfigId: venueMapInfo.venueCfgId,
      modelVersionId: selectedSectionRecommendations.modelVersionId,
      recommendedSelectedSectionSettings: recommendedSelectedSectionSettings,
    };
  }, [
    event?.viagId,
    recommendedSelectedSectionSettings,
    selectedListing?.id,
    selectedSectionRecommendations?.modelVersionId,
    venueMapInfo?.venueCfgId,
  ]);

  return {
    sectionRowScoreRecommendations,
  };
};

export const getMergedSelectedSectionSettings = (
  compListingSelectedSectionSettings?: SelectedSectionSettings | null,
  recommendedSelectedSectionSettings?: SelectedSectionSettings | null,
  force = false
) => {
  if (!recommendedSelectedSectionSettings) {
    return compListingSelectedSectionSettings;
  }
  if (
    !force &&
    compListingSelectedSectionSettings?.sectionIdFilter &&
    compListingSelectedSectionSettings?.sectionIdFilter.length > 0
  ) {
    return compListingSelectedSectionSettings;
  }
  const {
    sectionIdFilter,
    sectionRowIdFilter,
    rowIdFilter,
    rowOffset,
    ...rest
  } = compListingSelectedSectionSettings ?? ({} as SelectedSectionSettings);
  return {
    ...rest,
    rowOffset,
    sectionIdFilter: Array.from(
      new Set([
        ...(sectionIdFilter ?? []),
        ...(recommendedSelectedSectionSettings.sectionIdFilter ?? []),
      ])
    ),
    sectionRowIdFilter:
      rowOffset != null
        ? null
        : {
            ...(sectionRowIdFilter ?? {}),
            ...(recommendedSelectedSectionSettings.sectionRowIdFilter ?? {}),
          },
    rowIdFilter:
      rowOffset != null
        ? null
        : Array.from(
            new Set([
              ...(rowIdFilter ?? []),
              ...(recommendedSelectedSectionSettings.rowIdFilter ?? []),
            ])
          ),
  };
};

export const useOnePagerContainerViewMode = () => {
  const [forceToVerticalView, setForceToVerticalView] = useToggle(false);
  const [dynamicVerticalView, setDynamicVerticalView] = useToggle(false);
  const [onePagerContainerRef, { width }] = useMeasure<HTMLDivElement>();
  const { isOnSidePanel } = useEventHubContext();

  useEffect(() => {
    const shouldUserVerticalView = !isOnSidePanel && width < 815;
    if (shouldUserVerticalView !== dynamicVerticalView) {
      setDynamicVerticalView(shouldUserVerticalView);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnSidePanel, setDynamicVerticalView, width]);

  const onOnePagerContainerRefInitialized = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        const width = node.getBoundingClientRect().width;
        const shouldUserVerticalView = !isOnSidePanel && width < 815;
        setDynamicVerticalView(shouldUserVerticalView);
      }
    },
    [isOnSidePanel, setDynamicVerticalView]
  );

  const useVerticalView = useMemo(
    () => forceToVerticalView || dynamicVerticalView,
    [dynamicVerticalView, forceToVerticalView]
  );

  return {
    useVerticalView,
    setForceToVerticalView,
    onePagerContainerRef,
    onOnePagerContainerRefInitialized,
  };
};
